import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import { Loader2, Sparkles, TrendingUp, TrendingDown } from "lucide-react";

export default function Intelligence() {
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { api.get("/intelligence/sentiment").then(r => setData(r.data)); }, []);

  const runAI = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/intelligence/ai-analysis", {});
      setAnalysis(r.data.analysis);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI analysis failed"); }
    finally { setAiLoading(false); }
  };

  if (!data) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading tracking…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow={`Intelligence${data.entity_name ? ` · ${data.entity_name}` : ""}`}
        title="Polling & Sentiment"
        subtitle="Favorability tracking, issue sentiment, and mention volume — with AI narrative analysis."
        action={
          <Btn onClick={runAI} disabled={aiLoading} data-testid="intel-ai-btn">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI analysis
          </Btn>
        }
      />

      {analysis && (
        <Card className="border-cyan-400/30" data-testid="intel-ai-panel">
          <div className="text-xs uppercase tracking-wider text-cyan-300 font-medium mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Narrative Analysis</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{analysis}</div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="favorability-chart">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold">Favorability — 10 week tracking</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 uppercase">Demo tracking</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series}>
                <defs>
                  <linearGradient id="fav" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" />
                <XAxis dataKey="week" tick={{ fill: "var(--text-muted)", fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} unit="%" domain={[20, 70]} />
                <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="favorability" stroke="var(--accent-primary)" fill="url(#fav)" strokeWidth={2} />
                <Area type="monotone" dataKey="unfavorability" stroke="var(--danger)" fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-slate-600 mt-2">Solid: favorable · Dashed: unfavorable</div>
        </Card>

        <Card data-testid="issues-card">
          <h3 className="font-display text-lg font-bold mb-3">Issue sentiment</h3>
          <div className="space-y-3">
            {data.issues.map(i => (
              <div key={i.issue} className="flex items-center gap-3">
                {i.trend === "up" ? <TrendingUp className="w-4 h-4 text-emerald-300 shrink-0" /> : <TrendingDown className="w-4 h-4 text-rose-300 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{i.issue}</div>
                  <div className="text-[11px] text-slate-500">{i.volume.toLocaleString()} mentions</div>
                </div>
                <div className={`font-mono text-sm ${i.net_support >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {i.net_support > 0 ? "+" : ""}{i.net_support}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
